import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { OfficeKey } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';

const ADDABLE_OFFICES: { key: Exclude<OfficeKey, 'sinOffice'>; label: string }[] = [
  { key: 'buenosAires', label: 'Office Buenos Aires' }, // i18n-ok: nombre propio de la oficina
  { key: 'cordoba', label: 'Office Córdoba' }, // i18n-ok: nombre propio de la oficina
  { key: 'brasil', label: 'Office Brasil' }, // i18n-ok: nombre propio de la oficina
  { key: 'colombia', label: 'Office Colombia' }, // i18n-ok: nombre propio de la oficina
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** Oficinas que el usuario puede operar (del getMyAccess). */
  offices: OfficeKey[];
}

/**
 * Alta simplificada de un equipo que llegó a la oficina. clapBase, status
 * ("Lista para Entregar") y nro de referencia los fuerza el backend.
 */
export function OmAddDeviceDialog({ open, onClose, offices }: Props) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const selectable = ADDABLE_OFFICES.filter((o) => offices.includes(o.key));
  const [form, setForm] = useState({
    office: (selectable[0]?.key ?? 'buenosAires') as Exclude<OfficeKey, 'sinOffice'>,
    categoria: '',
    modelo: '',
    nroSerie: '',
    nroModelo: '',
    procesador: '',
    ramGB: '',
    discoGB: '',
    owner: '',
    comments: '',
  });

  const { data: options } = trpc.officeManager.getAddDeviceOptions.useQuery(undefined, {
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = trpc.officeManager.addDevice.useMutation({
    onSuccess: (device) => {
      toast.success(`Equipo cargado: ${device.modelo} (${device.nroSerie}) — ref ${device.nroReferencia}`);
      utils.officeManager.getBoard.invalidate();
      onClose();
      setForm((f) => ({ ...f, categoria: '', modelo: '', nroSerie: '', nroModelo: '', procesador: '', ramGB: '', discoGB: '', owner: '', comments: '' }));
    },
    onError: (err) => toast.error(err.message),
  });

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const canSubmit = form.categoria && form.modelo.trim() && form.nroSerie.trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('office-manager.omadddevicedialog.agregar_equipo_a_la_oficina')}</DialogTitle>
          <DialogDescription>
            {t('office-manager.omadddevicedialog.el_equipo_entra_al_inventario_como')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('office-manager.omadddevicedialog.oficina')}</Label>
              <Select
                value={form.office}
                onValueChange={(v) => setForm((f) => ({ ...f, office: v as Exclude<OfficeKey, 'sinOffice'> }))}
                disabled={selectable.length <= 1}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectable.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('office-manager.omadddevicedialog.categoria')}</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm((f) => ({ ...f, categoria: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Elegir..." />
                </SelectTrigger>
                <SelectContent>
                  {(options?.categorias ?? []).map((c: any) => (
                    <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="om-add-modelo">{t('office-manager.omadddevicedialog.modelo')}</Label>
              <Input id="om-add-modelo" value={form.modelo} onChange={set('modelo')} placeholder="MacBook Air M3" /> {/* i18n-ok: ejemplo de modelo */}
            </div>
            <div>
              <Label htmlFor="om-add-serie">{t('office-manager.omadddevicedialog.nro_de_serie')}</Label>
              <Input id="om-add-serie" value={form.nroSerie} onChange={set('nroSerie')} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="om-add-nromodelo">{t('office-manager.omadddevicedialog.nro_modelo')}</Label>
              <Input id="om-add-nromodelo" value={form.nroModelo} onChange={set('nroModelo')} placeholder="MK183LL/A" />
            </div>
            <div>
              <Label htmlFor="om-add-ram">{t('office-manager.omadddevicedialog.ram_gb')}</Label>
              <Input id="om-add-ram" type="number" value={form.ramGB} onChange={set('ramGB')} />
            </div>
            <div>
              <Label htmlFor="om-add-disco">{t('office-manager.omadddevicedialog.disco_gb')}</Label>
              <Input id="om-add-disco" type="number" value={form.discoGB} onChange={set('discoGB')} />
            </div>
          </div>
          <div>
            <Label htmlFor="om-add-owner">{t('office-manager.omadddevicedialog.owner_mail_del_ingreso_opcional')}</Label>
            <Input id="om-add-owner" type="email" value={form.owner} onChange={set('owner')} placeholder="nombre.apellido@winclap.com" />
          </div>
          <div>
            <Label htmlFor="om-add-comments">Comentarios</Label>
            <Input id="om-add-comments" value={form.comments} onChange={set('comments')} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!canSubmit || mutation.isPending}
            onClick={() =>
              mutation.mutate({
                office: form.office,
                categoria: form.categoria,
                modelo: form.modelo.trim(),
                nroSerie: form.nroSerie.trim(),
                nroModelo: form.nroModelo.trim() || undefined,
                procesador: form.procesador.trim() || undefined,
                ramGB: form.ramGB ? Number(form.ramGB) : undefined,
                discoGB: form.discoGB ? Number(form.discoGB) : undefined,
                owner: form.owner.trim() || undefined,
                comments: form.comments.trim() || undefined,
              })
            }
          >
            {mutation.isPending ? <WinclapLoader size={16} className="mr-2" /> : <PackagePlus className="mr-2 h-4 w-4" />}
            Agregar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
